<template>
  <el-dialog
    :visible.sync="visible"
    width="830px"
    top="8vh"
    append-to-body
    custom-class="goods-global-search-dialog"
    @opened="focusKeyword"
    @close="invalidateSearch"
  >
    <div slot="title" class="search-title">
      <span class="search-title-icon">🔍</span>
      <span>全局商品搜索</span>
      <span class="search-scope-badge">跨 5 类课程类型</span>
    </div>
    <div class="search-bar">
      <GoodsKeywordSearch ref="keywordSearch" v-model="search" class="global-search-keyword" dropdown-class="goods-global-search-options" @search="submitSearch" />
      <el-button type="primary" size="small" :loading="loading" @click="submitSearch">搜索</el-button>
    </div>
    <div v-loading="loading" class="search-results" aria-live="polite">
      <div v-if="error" class="empty-state">
        <p>搜索失败，请重试</p>
        <el-button size="small" @click="fetchResults">重新搜索</el-button>
      </div>
      <div v-else-if="!submitted" class="empty-state">
        <span class="empty-state-icon" aria-hidden="true">📚</span>
        <p>本页面支持根据筛选条件搜索专栏、大专栏、线下课、训练营、套餐课</p>
      </div>
      <div v-else-if="!loading && !records.length" class="empty-state">未找到匹配商品，请调整关键词</div>
      <template v-else>
        <p class="result-count">共找到 {{ total }} 个商品</p>
        <div v-for="row in records" :key="row.goodsId" class="result-item" @click="openDetails(row, $event)">
          <el-image class="goods-cover" :src="row.coverVerticalUrl || row.coverUrl || row.patchUrl" fit="cover">
            <div slot="error" class="cover-placeholder"><i class="el-icon-reading" /></div>
          </el-image>
          <div class="goods-info">
            <a v-if="canView(row)" :href="goodsLink(row)" target="_blank" rel="noopener" class="goods-name">{{ row.contentName }}</a>
            <span v-else class="goods-name">{{ row.contentName }}</span>
            <div class="goods-meta">
              <el-tag size="mini">{{ typeLabel(row) }}</el-tag>
              <span>ID：{{ row.goodsId }}</span>
            </div>
            <div class="goods-meta">
              <span class="goods-price">{{ priceLabel(row) }}</span>
              <span>{{ shelfLabels[row.shelveFlag] || '未知状态' }}</span>
            </div>
            <div v-if="row.uniqueCode" class="unique-code">唯一编码：{{ row.uniqueCode }}</div>
          </div>
          <div class="goods-actions">
            <a v-if="canView(row)" :href="goodsLink(row)" target="_blank" rel="noopener">详情</a>
            <a v-if="canEdit(row)" :href="goodsLink(row, true)" target="_blank" rel="noopener">编辑</a>
          </div>
        </div>
      </template>
    </div>
    <div class="search-footer">
      <span><kbd>Enter</kbd> 搜索</span>
      <el-pagination
        v-if="submitted && !error && total > pageSize"
        :current-page="current"
        :page-size="pageSize"
        :total="total"
        small
        layout="prev, pager, next"
        @current-change="changePage"
      />
      <span class="close-hint"><kbd>Esc</kbd> 关闭</span>
    </div>
  </el-dialog>
</template>

<script>
import { getGoodsList } from '@/api/course'
import GoodsKeywordSearch from './GoodsKeywordSearch.vue'
import { GOODS_SEARCH_TYPES, canEditGoods, findGoodsRoute, goodsSearchParams } from './goodsSearch'

export default {
  components: { GoodsKeywordSearch },
  data() {
    return {
      visible: false,
      search: { matchField: 'contentName', keyword: '' },
      appliedSearch: null,
      submitted: false,
      loading: false,
      error: false,
      records: [],
      total: 0,
      current: 1,
      pageSize: 20,
      requestId: 0,
      shelfLabels: ['已上架', '已下架', '待上架']
    }
  },
  computed: {
    routes() {
      return this.$store.getters.permission_routes
    }
  },
  mounted() {
    this.$root.$on('open-goods-search', this.open)
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    this.$root.$off('open-goods-search', this.open)
    document.removeEventListener('keydown', this.onKeydown)
    this.invalidateSearch()
  },
  methods: {
    open(value) {
      if (value) {
        this.invalidateSearch()
        this.search = { ...value }
        this.submitted = false
        this.records = []
        this.total = 0
        this.error = false
      }
      this.visible = true
      this.$nextTick(this.focusKeyword)
    },
    focusKeyword() {
      if (this.$refs.keywordSearch) this.$refs.keywordSearch.focus()
    },
    onKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        if (!GOODS_SEARCH_TYPES.some(type => findGoodsRoute(this.routes, type.listPath))) return
        event.preventDefault()
        this.open()
      }
    },
    invalidateSearch() {
      if (this.loading) {
        this.submitted = false
        this.records = []
        this.total = 0
      }
      this.requestId++
      this.loading = false
    },
    submitSearch() {
      const keyword = this.search.keyword.trim()
      if (!keyword) {
        this.$message.warning('请输入搜索内容')
        this.focusKeyword()
        return
      }
      this.appliedSearch = goodsSearchParams(this.search)
      if (Object.keys(this.appliedSearch).length !== 1) return
      this.current = 1
      this.fetchResults()
    },
    changePage(current) {
      this.current = current
      this.fetchResults()
    },
    async fetchResults() {
      if (!this.appliedSearch) return
      const requestId = ++this.requestId
      this.loading = true
      this.error = false
      this.submitted = true
      this.records = []
      try {
        const { data } = await getGoodsList({
          ...this.appliedSearch,
          combinationType: 2,
          current: this.current,
          size: this.pageSize
        })
        if (requestId !== this.requestId) return
        this.records = (data && data.records) || []
        this.total = Number(data && data.total) || 0
      } catch (error) {
        if (requestId !== this.requestId) return
        this.error = true
        this.total = 0
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    goodsType(row) {
      return GOODS_SEARCH_TYPES.find(type => type.value === Number(row.goodsType))
    },
    typeLabel(row) {
      const type = this.goodsType(row)
      return type ? type.label : ''
    },
    canView(row) {
      const type = this.goodsType(row)
      const route = type && findGoodsRoute(this.routes, type.listPath)
      if (!route) return false
      return ![7, 8].includes(type.value) || ((route.meta || {}).permsList || []).includes('system:goods:detail')
    },
    canEdit(row) {
      return canEditGoods(this.routes, row.goodsType)
    },
    goodsLink(row, edit = false) {
      const type = this.goodsType(row)
      if (!type) return ''
      return this.$router.resolve({
        path: `${type.detailPath}/${encodeURIComponent(row.goodsId)}`,
        query: edit ? { edit: '1' } : {}
      }).href
    },
    openDetails(row, event) {
      if (!this.canView(row) || event.target.closest('a')) return
      window.open(this.goodsLink(row), '_blank', 'noopener')
    },
    priceLabel(row) {
      if (Number(row.goodsType) !== 5 && row.saleType === 0) return '免费'
      if (row.salePrice == null) return Number(row.goodsType) === 5 ? '未配置规格价格' : '暂无价格'
      return `￥${(row.salePrice / 100).toFixed(2)}${Number(row.goodsType) === 5 ? '起' : ''}`
    }
  }
}
</script>

<style lang="scss" scoped>
.search-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #15233e;
  font-size: 13px;
  font-weight: 600;
}
.search-title-icon {
  display: grid;
  place-items: center;
  width: 21px;
  height: 21px;
  border-radius: 6px;
  background: #3e5bff;
  font-size: 13px;
}
.search-scope-badge {
  padding: 2px 8px;
  border: 1px solid #ffdad2;
  border-radius: 12px;
  color: #ff4d4f;
  background: #fff5f2;
  font-size: 10px;
  font-weight: 400;
}
.search-bar {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 16px 15px;
  background: linear-gradient(180deg, #f8faff, #fff);
  border-bottom: 1px solid #edf0f7;
  .goods-keyword-search { flex: 1; width: auto; min-width: 0; }
  > .el-button {
    flex: 0 0 74px;
    height: 40px;
    border: 0;
    border-radius: 9px;
    background: linear-gradient(135deg, #496aff, #3151f4);
    box-shadow: 0 4px 10px rgba(49, 81, 244, .25);
    font-size: 12px;
    font-weight: 600;
  }
}
.result-count { margin: 12px 16px; color: #8e9ab1; font-size: 12px; }
.search-results { max-height: min(390px, 55vh); min-height: 178px; overflow-y: auto; }
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 178px;
  padding: 24px 20px;
  box-sizing: border-box;
  text-align: center;
  color: #8e9ab1;
  font-size: 12px;
  p { margin: 17px 0 0; line-height: 20px; }
}
.empty-state-icon {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border-radius: 17px;
  background: #eaf0ff;
  font-size: 25px;
}
.result-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 8px;
  border-bottom: 1px solid #ebeef5;
  &:hover { background: #f7f9fc; }
}
.goods-cover { width: 64px; height: 72px; flex-shrink: 0; border-radius: 4px; }
.cover-placeholder { height: 100%; display: grid; place-items: center; background: #f5f7fa; color: #909399; font-size: 28px; }
.goods-info { min-width: 0; flex: 1; }
.goods-name { color: #303133; font-weight: 500; overflow-wrap: anywhere; }
.goods-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-top: 8px; font-size: 12px; color: #909399; }
.goods-price { color: #f56c6c; }
.unique-code { margin-top: 6px; font-size: 12px; color: #909399; overflow-wrap: anywhere; }
.goods-actions { display: flex; gap: 16px; flex-shrink: 0; a { color: #409eff; } }
.search-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 35px;
  padding: 8px 18px;
  box-sizing: border-box;
  border-top: 1px solid #edf0f7;
  background: #fafbfe;
  color: #8e9ab1;
  font-size: 10px;
  .close-hint { margin-left: auto; }
  .el-pagination { margin-left: auto; }
  kbd {
    padding: 0 4px;
    border: 1px solid #e0e6f1;
    border-radius: 3px;
    background: #fff;
    font: inherit;
  }
}
</style>

<style lang="scss">
.goods-global-search-dialog {
  max-width: calc(100vw - 32px);
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 18px 60px rgba(25, 40, 75, .2);
  .el-dialog__header {
    padding: 18px 16px 14px;
    background: #f8faff;
  }
  .el-dialog__headerbtn { top: 21px; right: 24px; font-size: 12px; }
  .el-dialog__body { padding: 0; }
  .global-search-keyword {
    height: 40px;
    max-width: 100%;
    border: 1px solid #dce2f0;
    border-radius: 9px;
    background: #fff;
    .goods-search-field {
      flex: 0 0 112px;
      width: 112px;
      .el-input__inner {
        height: 38px;
        padding-left: 12px;
        border: 0;
        border-right: 1px solid #e3e8f3;
        border-radius: 8px 0 0 8px;
        background: #fff;
        color: #4560ff;
        font-weight: 600;
        font-size: 12px;
        &:focus { box-shadow: inset 0 0 0 1px #5268ff; }
      }
      .el-input__suffix { color: #4560ff; }
      .el-input__icon { line-height: 38px; }
    }
    .goods-search-keyword .el-input__inner {
      height: 38px;
      border: 0;
      border-radius: 0 8px 8px 0;
      font-size: 12px;
      background: transparent;
    }
  }
}
.goods-global-search-options.el-select-dropdown {
  min-width: 140px !important;
  margin-top: 6px;
  padding: 3px;
  border: 1px solid #e8ecf5;
  border-radius: 9px;
  box-shadow: 0 12px 26px rgba(28, 42, 65, .16);
  .el-select-dropdown__list { padding: 0; }
  .el-select-dropdown__item {
    height: 31px;
    padding: 0 12px;
    border-radius: 5px;
    line-height: 31px;
    font-size: 12px;
    &.selected, &.hover, &:hover { background: #eef1ff; color: #4560ff; }
  }
  .popper__arrow { display: none; }
}
</style>
